// Frontend Security Utilities
class FrontendSecurity {
  constructor() {
    this.init();
  }

  init() {
    // Initialize security measures - simplified, no environment checks
    this.setupCSRFProtection();
    this.setupXSSProtection();
    this.setupContentSecurityPolicy();
    this.setupSessionSecurity();
    this.setupErrorHandling();
    
    // Keep it simple - no production/development differences
    this.disableDevTools();
  }

  // CSRF Protection
  setupCSRFProtection() {
    // Generate CSRF token for session
    this.csrfToken = this.generateCSRFToken();
    
    // Store in session storage (safer than localStorage for sensitive data)
    sessionStorage.setItem('csrf_token', this.csrfToken);
  }

  generateCSRFToken() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  getCSRFToken() {
    return sessionStorage.getItem('csrf_token') || this.csrfToken;
  }

  // XSS Protection
  setupXSSProtection() {
    // Sanitize user inputs
    this.inputSanitizer = this.createInputSanitizer();
  }

  createInputSanitizer() {
    return {
      // Basic HTML sanitization
      sanitizeHTML: (input) => {
        const div = document.createElement('div');
        div.textContent = input;
        return div.innerHTML;
      },

      // URL sanitization
      sanitizeURL: (url) => {
        try {
          const parsedUrl = new URL(url);
          // Only allow http and https protocols
          if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            return null;
          }
          return parsedUrl.toString();
        } catch {
          return null;
        }
      },

      // Remove dangerous characters
      sanitizeInput: (input) => {
        if (typeof input !== 'string') return input;
        
        return input
          .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
          .replace(/javascript:/gi, '') // Remove javascript: protocol
          .replace(/vbscript:/gi, '') // Remove vbscript: protocol
          .replace(/data:/gi, '') // Remove data: protocol (can be dangerous)
          .replace(/on\w+\s*=/gi, '') // Remove event handlers
          .trim();
      }
    };
  }

  // Content Security Policy helpers
  setupContentSecurityPolicy() {
    // Report CSP violations - no environment check
    document.addEventListener('securitypolicyviolation', (e) => {
      this.reportSecurityViolation('CSP_VIOLATION', {
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        originalPolicy: e.originalPolicy,
        referrer: e.referrer
      });
    });
  }

  // Session Security
  setupSessionSecurity() {
    // Monitor for session hijacking attempts
    this.sessionFingerprint = this.generateSessionFingerprint();
    
    // Check session integrity periodically - simplified
    setInterval(() => {
      this.validateSessionIntegrity();
    }, 120000); // Check every 2 minutes

    // Clear sensitive data on page unload
    window.addEventListener('beforeunload', () => {
      this.clearSensitiveData();
    });

    // Detect tab/window changes for security
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.onTabHidden();
      } else {
        this.onTabVisible();
      }
    });
  }

  generateSessionFingerprint() {
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      // Removed timestamp to avoid false positives in session validation
    };

    return btoa(JSON.stringify(fingerprint));
  }

  validateSessionIntegrity() {
    // Simple session validation - no complex environment checks
    const storedFingerprint = sessionStorage.getItem('session_fingerprint');
    const currentFingerprint = this.generateSessionFingerprint();
    
    if (!storedFingerprint) {
      sessionStorage.setItem('session_fingerprint', currentFingerprint);
      return;
    }
    
    if (storedFingerprint !== currentFingerprint) {
      try {
        const stored = JSON.parse(atob(storedFingerprint));
        const current = JSON.parse(atob(currentFingerprint));
        
        // Check if any critical fingerprint elements changed
        const criticalChanges = (
          stored.userAgent !== current.userAgent ||
          stored.platform !== current.platform ||
          stored.screen !== current.screen ||
          stored.timezone !== current.timezone
        );
        
        if (criticalChanges) {
          this.reportSecurityViolation('SESSION_HIJACKING_ATTEMPT', {
            stored: storedFingerprint,
            current: currentFingerprint
          });
          
          // Simple handling - no complex breach logic
          console.warn('Session validation failed - potential security issue');
        }
      } catch (error) {
        sessionStorage.setItem('session_fingerprint', currentFingerprint);
      }
    }
  }

  // Error Handling Security
  setupErrorHandling() {
    // Temporarily disabled to prevent error cascade
    // Global error handler - simplified
    window.addEventListener('error', (event) => {
      // Log error but don't clear console or prevent display
      console.warn('Error caught by security handler:', event.error);
      // Temporarily disabled: this.reportSecurityEvent('CLIENT_ERROR', {
      //   message: 'An error occurred',
      //   timestamp: new Date().toISOString()
      // });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      console.warn('Unhandled rejection caught by security handler:', event.reason);
      // Temporarily disabled: this.reportSecurityEvent('UNHANDLED_PROMISE_REJECTION', {
      //   timestamp: new Date().toISOString()
      // });
    });
  }

  // Remove this function completely - it was causing issues
  // setupProductionSecurity() - REMOVED

  detectDevTools() {
    // Simple dev tools detection - no redirects, no environment checks
    let devtools = {
      open: false,
      orientation: null
    };

    const threshold = 160;

    setInterval(() => {
      if (
        window.outerHeight - window.innerHeight > threshold ||
        window.outerWidth - window.innerWidth > threshold
      ) {
        if (!devtools.open) {
          devtools.open = true;
          this.reportSecurityViolation('DEV_TOOLS_OPENED');
          // NO REDIRECTS - just log it
        }
      } else {
        devtools.open = false;
      }
    }, 500);
  }

  disableDevTools() {
    // Simple console disabling - no environment checks
    const disabledMethods = [
      'log', 'debug', 'info', 'warn', 'error', 'assert', 'dir', 'dirxml',
      'group', 'groupEnd', 'time', 'timeEnd', 'count', 'trace', 'profile',
      'profileEnd', 'table', 'clear'
    ];

    disabledMethods.forEach(method => {
      console[method] = function() {};
    });
  }

  // Security Event Reporting
  reportSecurityViolation(type, details = {}) {
    const violation = {
      type,
      details,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer
    };

    // Send to backend security logging endpoint
    this.sendSecurityReport('violation', violation);
  }

  reportSecurityEvent(type, details = {}) {
    const event = {
      type,
      details,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    this.sendSecurityReport('event', event);
  }

  async sendSecurityReport(category, data) {
    try {
      // Rate limiting: prevent spam
      const now = Date.now();
      if (!this.lastReportTime) this.lastReportTime = {};
      if (this.lastReportTime[category] && (now - this.lastReportTime[category]) < 5000) {
        return; // Skip if last report was less than 5 seconds ago
      }
      this.lastReportTime[category] = now;
      
      // Always send reports - no environment checks
      await fetch('/api/v1/security/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          category,
          data
        })
      });
    } catch (error) {
      // Silently fail - don't expose errors in security reporting
    }
  }

  // Security Utilities
  clearSensitiveData() {
    // Clear sensitive data from memory and storage
    sessionStorage.removeItem('csrf_token');
    sessionStorage.removeItem('session_fingerprint');
    
    // Clear any cached sensitive data
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          if (name.includes('sensitive') || name.includes('auth')) {
            caches.delete(name);
          }
        });
      });
    }
  }

  handleSecurityBreach() {
    // Simple security breach handling - no redirects, no loops
    const currentPath = window.location.pathname;
    
    // Don't do anything if already on login page
    if (currentPath === '/auth/login' || currentPath === '/login') {
      return;
    }
    
    // Just clear token and log - no redirects
    localStorage.removeItem('token');
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    console.warn('Security issue detected. Please refresh and log in again.');
    
    // NO AUTOMATIC REDIRECTS - let user handle it
  }

  onTabHidden() {
    // Implement security measures when tab becomes hidden
    this.tabHiddenTime = Date.now();
  }

  onTabVisible() {
    // Check if tab was hidden too long (potential security risk)
    if (this.tabHiddenTime) {
      const hiddenDuration = Date.now() - this.tabHiddenTime;
      
      // If hidden for more than 30 minutes, validate session
      if (hiddenDuration > 30 * 60 * 1000) {
        this.validateSessionIntegrity();
      }
    }
  }

  // Utility methods for components
  sanitizeUserInput(input) {
    return this.inputSanitizer.sanitizeInput(input);
  }

  sanitizeHTML(html) {
    return this.inputSanitizer.sanitizeHTML(html);
  }

  sanitizeURL(url) {
    return this.inputSanitizer.sanitizeURL(url);
  }

  isSecureContext() {
    return window.isSecureContext || location.protocol === 'https:';
  }

  // Initialize security monitoring
  static init() {
    if (!window.frontendSecurity) {
      window.frontendSecurity = new FrontendSecurity();
    }
    return window.frontendSecurity;
  }
}

// Initialize security when module loads
const security = FrontendSecurity.init();

export default security;
